import { Button, Card, StatusMessage, TextInput } from "@herefor/design-system";
import { INVITATION_LIFECYCLE_MESSAGES } from "@herefor/product-language";
import type { SyntheticEvent } from "react";

import type { InvitationListResult } from "../../convex/invitations";

type Invitation = InvitationListResult["invitations"][number];

export type InvitationAdminOutcome =
  | "idle"
  | "working"
  | "sent"
  | "invalid_name"
  | "invalid_email"
  | "already_invited"
  | "unavailable";

export interface InvitationAdminProps {
  readonly invitations: readonly Invitation[];
  readonly outcome: InvitationAdminOutcome;
  readonly actionsDisabled?: boolean;
  readonly onIssue: (chosenName: string, email: string) => Promise<void>;
  readonly onResend: (invitationId: Invitation["invitationId"]) => Promise<void>;
  readonly onRevoke: (invitationId: Invitation["invitationId"]) => Promise<void>;
  /** Standalone by default; level two when composed into the settings page. */
  readonly headingLevel?: 1 | 2;
}

export function InvitationAdmin({
  invitations,
  outcome,
  actionsDisabled = false,
  onIssue,
  onResend,
  onRevoke,
  headingLevel = 1,
}: InvitationAdminProps) {
  const copy = INVITATION_LIFECYCLE_MESSAGES.admin;
  const busy = actionsDisabled || outcome === "working";
  const Heading = headingLevel === 1 ? "h1" : "h2";
  const cardHeadingLevel = headingLevel === 1 ? 2 : 3;
  return (
    <section className="invitation-admin" data-invitation-admin>
      <Heading>{copy.heading}</Heading>
      <p>{copy.explanation}</p>
      <Card title={copy.heading} titleLevel={cardHeadingLevel}>
        <form
          onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
            event.preventDefault();
            const form = event.currentTarget;
            const values = new FormData(form);
            const name = values.get("invitationName");
            const email = values.get("invitationEmail");
            void onIssue(
              typeof name === "string" ? name : "",
              typeof email === "string" ? email : "",
            );
            form.reset();
          }}
        >
          <TextInput disabled={busy} label={copy.nameLabel} name="invitationName" required />
          <TextInput
            disabled={busy}
            label={copy.emailLabel}
            name="invitationEmail"
            required
            type="email"
          />
          <Button disabled={busy} type="submit" variant="secondary">
            {outcome === "working" ? copy.sending : copy.action}
          </Button>
        </form>
        <InvitationOutcome outcome={outcome} />
      </Card>

      <Card title={copy.currentHeading} titleLevel={cardHeadingLevel}>
        {invitations.length === 0 ? (
          <p>{copy.empty}</p>
        ) : (
          <ul>
            {invitations.map((invitation) => (
              <li key={invitation.invitationId}>
                <span>{invitation.chosenName}</span> <span>{invitation.maskedDestination}</span>{" "}
                <span>{copy.state[invitation.state]}</span>{" "}
                {invitation.state === "ready" ? (
                  <>
                    <Button
                      disabled={busy}
                      onClick={() => {
                        void onResend(invitation.invitationId);
                      }}
                      variant="secondary"
                    >
                      {copy.resendAction}
                    </Button>{" "}
                    <Button
                      disabled={busy}
                      onClick={() => {
                        void onRevoke(invitation.invitationId);
                      }}
                      variant="secondary"
                    >
                      {copy.revokeAction}
                    </Button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}

function InvitationOutcome({ outcome }: { readonly outcome: InvitationAdminOutcome }) {
  if (outcome === "idle" || outcome === "working") return null;
  const copy = INVITATION_LIFECYCLE_MESSAGES.admin;
  const message = {
    sent: copy.sent,
    invalid_name: copy.invalidName,
    invalid_email: copy.invalidEmail,
    already_invited: copy.alreadyInvited,
    unavailable: copy.unavailable,
  }[outcome];
  return <StatusMessage tone={outcome === "sent" ? "success" : "info"}>{message}</StatusMessage>;
}
